if (!customElements.get('shop-the-look-grid-slider')) {
  customElements.define('shop-the-look-grid-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.products = Array.from(this.querySelectorAll('[data-slider-product]'));
      this.hotspots = Array.from(this.querySelectorAll('[data-product-index]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dots] button'));
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.addAllButton = this.querySelector('[data-add-all]');
      this.addAllError = this.querySelector('[data-add-all-error]');
      this.productsPerPage = Number(this.dataset.productsPerPage) || 1;
      this.pageCount = Math.ceil(this.products.length / this.productsPerPage);
      this.currentPage = 0;

      if (!this.viewport || this.products.length === 0) return;

      this.previousButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
      this.addAllButton?.addEventListener('click', () => this.addAllProducts());

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goToPage(Number(dot.dataset.carouselPage)));
      });

      let scrollTimer;
      this.viewport.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.syncPageFromScroll(), 100);
      }, { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.goToPage(this.currentPage, false));
      this.resizeObserver.observe(this.viewport);
      this.updateControls();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    goToPage(page, smooth = true) {
      const targetPage = Math.min(Math.max(page, 0), this.pageCount - 1);
      const targetProduct = this.products[targetPage * this.productsPerPage];

      if (!targetProduct) return;

      this.currentPage = targetPage;
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      const productLeft = targetProduct.getBoundingClientRect().left;
      const targetLeft = this.viewport.scrollLeft + productLeft - viewportLeft;

      this.viewport.scrollTo({
        left: targetLeft,
        behavior: smooth ? 'smooth' : 'auto'
      });

      this.updateControls();
    }

    syncPageFromScroll() {
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      let closestProductIndex = 0;
      let closestDistance = Infinity;

      this.products.forEach((product, index) => {
        const distance = Math.abs(product.getBoundingClientRect().left - viewportLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestProductIndex = index;
        }
      });

      this.currentPage = Math.min(Math.floor(closestProductIndex / this.productsPerPage), this.pageCount - 1);
      this.updateControls();
    }

    updateControls() {
      if (this.previousButton) this.previousButton.disabled = this.currentPage === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentPage === this.pageCount - 1;

      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.currentPage ? 'true' : 'false');
      });

      const firstVisibleIndex = this.currentPage * this.productsPerPage;
      const lastVisibleIndex = firstVisibleIndex + this.productsPerPage;

      this.hotspots.forEach((hotspot) => {
        const productIndex = Number(hotspot.dataset.productIndex);
        hotspot.hidden = productIndex < firstVisibleIndex || productIndex >= lastVisibleIndex;
      });
    }

    async addAllProducts() {
      const items = this.products
        .filter((product) => product.dataset.addAllVariantId)
        .map((product) => ({ id: Number(product.dataset.addAllVariantId), quantity: 1 }));

      if (items.length === 0 || this.addAllButton.disabled) return;

      this.addAllButton.disabled = true;
      this.addAllButton.setAttribute('aria-busy', 'true');
      this.addAllError.hidden = true;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();

        if (!response.ok) throw new Error(result.description || 'No se pudieron agregar los productos al carrito.');

        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = result.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
        }));
      } catch (error) {
        this.addAllError.textContent = error.message || 'No se pudieron agregar los productos al carrito.';
        this.addAllError.hidden = false;
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this.addAllButton.disabled = false;
        this.addAllButton.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  });
}
